import { Fragment } from "react";

import Divider from "@mui/material/Divider";

import ErrorState from "@app/analytics/components/ErrorState";
import { WpmGradientRange } from "@app/analytics/components/GradientRange/CoachingGradientRange";
import TableCell from "@app/analytics/components/Table/TableCell";
import { scaleWpmDescription } from "@app/analytics/utils";
import { getWpmDescription } from "@app/analytics/utils/coachingUtils";
import { HeadCell } from "@app/common/hooks/useDataTable";
import { CoachingSession } from "@app/common/types";
import { getWpmSemanticScore } from "@app/common/utils/coachingUtils";

import { Column, Row } from "@rui/Stack";

import CoachingMoments, {
  CoachingMomentProps,
} from "./components/CoachingMoments";
import CoachingTable from "./components/CoachingTable";
import useCoachingDashboardData from "./hooks/useCoachingDashboardData";
import {
  SectionDescription,
  SectionHeading,
  Card,
  SectionSubHeading,
  SectionOverviewContainer,
  ValueWithSemanticIcon,
  EmptyCardText,
  LoadingSkeleton,
} from "./styles";

const WPM_HEAD_CELLS: HeadCell<CoachingSession>[] = [
  {
    id: "rate",
    label: "WPM",
  },
  {
    id: "secondaryRate",
    label: "% Filler",
  },
];

const getMomentLabel: CoachingMomentProps["getMomentLabel"] = (moment) =>
  `${scaleWpmDescription(moment.rate)} (${moment.rate} wpm)`;

const TalkingPace = () => {
  const { talkingPace, isError, isInitialLoading, refetch } =
    useCoachingDashboardData();

  const wpm = talkingPace.rate;
  const descriptionSegments = getWpmDescription(wpm);

  if (isError) {
    return <ErrorState refetch={refetch} />;
  }

  return (
    <>
      <Column spacing={1}>
        <Row variant="apart">
          <SectionHeading>Talking pace</SectionHeading>
          {wpm != null && (
            <ValueWithSemanticIcon
              score={getWpmSemanticScore(wpm)}
              variant="h3"
              sx={{ fontWeight: 500 }}
            >
              {wpm} wpm
            </ValueWithSemanticIcon>
          )}
        </Row>
        <SectionDescription>
          {descriptionSegments.map((segment) =>
            segment.style ? (
              <span key={segment.value} style={segment.style}>
                {segment.value}
              </span>
            ) : (
              <Fragment key={segment.value}>{segment.value}</Fragment>
            )
          )}
        </SectionDescription>
        <SectionOverviewContainer>
          <Card title="Range" subtitle="Your pace / meeting">
            {isInitialLoading ? (
              <LoadingSkeleton width="100%" />
            ) : wpm ? (
              <WpmGradientRange rate={wpm} />
            ) : (
              <EmptyCardText />
            )}
          </Card>
        </SectionOverviewContainer>
      </Column>
      <Divider />
      <Column spacing={1}>
        <SectionSubHeading>Moments</SectionSubHeading>
        <SectionDescription>
          Review your moments with talking speed outside of the target zone.
        </SectionDescription>
        <CoachingMoments
          getMomentLabel={getMomentLabel}
          isLoading={isInitialLoading}
          moments={talkingPace.moments}
          type="wpm"
        />
      </Column>
      <Divider />
      <Column spacing={1}>
        <SectionSubHeading>Meetings</SectionSubHeading>
        <SectionDescription>
          Review meeting reports where your talking speed was outside of the
          target zone.
        </SectionDescription>
        <CoachingTable
          isLoading={isInitialLoading}
          rows={talkingPace.sessions}
          sectionHeadCells={WPM_HEAD_CELLS}
          renderSectionCells={(session) => (
            <>
              <TableCell>
                <ValueWithSemanticIcon
                  score={getWpmSemanticScore(session.rate)}
                  variant="bodySecondaryReg"
                  sx={{ minWidth: "30px", textAlign: "right" }}
                >
                  {session.rate}
                </ValueWithSemanticIcon>
              </TableCell>
              <TableCell>
                {session.secondaryRate
                  ? `${Math.round(session.secondaryRate)}%`
                  : "-"}
              </TableCell>
            </>
          )}
        />
      </Column>
    </>
  );
};

export default TalkingPace;
